import { DrawCustomModeThis, DrawFeature, DrawPoint } from "@mapbox/mapbox-gl-draw";

import { LayerIds } from "fond/types";

import { pullInFeatures, refreshSnappedStatus, splitPaths } from "./helper";

/**
 * onCreate callback for underground points.
 *
 * Called by the base drawPoint.onClick after the point coordinates are set.
 * Handles:
 * 1. If placed on an existing underground point → delete the new one (dedup)
 * 2. If placed on an underground path → split the path at the point
 * 3. Refreshes snapped status of the new point
 * 4. Fires draw.create event
 */
export function onCreateUndergroundPoint(this: DrawCustomModeThis, feature: DrawFeature, state: Record<string, unknown>): void {
  const newPoint = feature as DrawPoint;

  // Set the layerId on the point so it gets correct styling.
  // The base drawPoint mode creates state.point without a layerId;
  // since we removed getFeatureProperties (which would conflict with onCreate),
  // we set it directly here.
  newPoint.setProperty("layerId", LayerIds.inStreetPoints);

  // Check for collocated underground features
  const { lines, points } = pullInFeatures.call(this, newPoint);

  if (points.length > 0) {
    // Point on existing underground point — delete the new one to avoid duplicates
    this.deleteFeature(newPoint.id as string, { silent: true });
    return;
  }

  if (lines.length > 0) {
    // Point on underground path(s) — split each path at this location.
    // Multiple lines can exist at a T-intersection; we must attempt to split all of them.
    lines.forEach((line) => splitPaths.call(this, line, newPoint));
  }

  // Refresh snapped status (will be snapped if paths were found)
  refreshSnappedStatus.call(this, newPoint);

  // Fire draw.create synchronously so it's handled by handleOnCreate (which
  // captures history via updateHistory) BEFORE the mode transition. Previously
  // this was deferred to wait for splitPaths' deferred deletion, but splitPaths
  // now deletes synchronously. Deferring here would cause draw.create to fire
  // after changeMode completes, at which point the new drawing mode's
  // placeholder features exist and would be captured in history.
  this.map.fire("draw.create", { features: [newPoint.toGeoJSON()] });
}
